
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";
import EmptyCard from "@/components/EmptyCard.vue";
import FooterMobile from "@/components/FooterMobile.vue";
import HeaderSecondary from "@/components/headers/HeaderSecondary.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import SearchMobile from "@/components/SearchMobile.vue";
import SidebarSearch from "@/components/sidebars/SidebarSearch.vue";
import TenderCard from "@/components/TenderCard.vue";
import ThePagination from "@/components/ThePagination.vue";
import { prepareSearchPayload, prepareQueryParams } from "@/helpers/SearchHelper";
import { trackEvent, EventType } from "@/helpers/AmplitudeHelper";


export default defineComponent({
  name: "CompanyView",
  components: {
    EmptyCard,
    FooterMobile,
    HeaderSecondary,
    LoadingSpinner,
    SearchMobile,
    SidebarSearch,
    TenderCard,
    ThePagination,
  },
  data() {
    let pageRefreshed = false;
    if (typeof window !== 'undefined') {
      const entries = window.performance.getEntriesByType('navigation') as Array<PerformanceNavigationTiming>
      if (entries.length > 0 && entries[0].type === 'reload') {
        pageRefreshed = true;
      }
    }
    return {
      pageIsRefreshed: pageRefreshed,
    };
  },
  computed: {
    ...mapGetters(["getTenders", "filters", "isLoading", "query", "page", "company", "hasSearchedTenders", "queryCompany", "filtersApplied"]),
    queryCompany: {
      get() {
        return this.$store.getters.queryCompany;
      },
      set(value: string) {
        this.$store.dispatch('setQueryCompany', { query: value });
      }
    },
    fetchData() {
      return this.$store.getters.filtersApplied('company')
    },
    metaTitle() {
      return `${this.$store.getters.company.title} компаниясининг харидлари (ИНН ${this.$store.getters.company.tin})`
    },
    metaDescription() {
      return  `${this.$store.getters.company.title} компаниясининг харидлари, тендерлари, телефони ва электрон почтаси (СТИР ${this.$store.getters.company.tin}) Tender.asiaда`
    },
    metaCanonicalLink() {
      const { path, query } = this.$route

      const sortedKeys = Object.keys(query).sort()
      const searchParams = new URLSearchParams()
      for (const key of sortedKeys) {
        const val = query[key]
        if (val == null) continue
        searchParams.append(key, val as string)
      }

      const qs = searchParams.toString()
      return `https://tender.asia${path}${qs ? `?${qs}` : ''}`
    },
  },
  methods: {
    ...mapActions(["getNewTenders", "searchTenders", "resetFilters", "setPage", "setQuery", "getCompany", "setFiltersApplied", "setIsLoading", "setQueryCompany"]),
    async handleCompanySearch() {
      const queryParams = prepareQueryParams(1, this.queryCompany, this.filters, this.$store.getters.contentFilter)
      this.$router.push({
          name: "company",
          query: queryParams
        })
    }
  },
  watch: {
    async fetchData(newValue) {
      if (newValue) {
        const queryParams = prepareQueryParams(1, this.queryCompany, this.filters, this.$store.getters.contentFilter)
        this.$router.push({
          name: "company",
          query: queryParams
        })
        this.setFiltersApplied({ applied: false })
      }
    },
    "$route.query": {
      async handler(queryParams) {
        this.setPage({ page: queryParams["page"] })

        const categoryOption = this.$store.getters.categoryOption
        this.resetFilters({ shouldApplyFilter: false })

        const companyTin = this.$route.params.companyTin
        if (this.company === null || (this.company && this.company.tin !== companyTin)) {          
          const result = await this.getCompany({ company_tin: companyTin })

          if (result === null) {
            this.$router.replace({path: '/404'})
            return
          }
        }

        this.$store.dispatch("updateFilter", {
          key: "company",
          value: this.company.tin
        });
        
        if ("q" in queryParams) {
          this.setQuery({ query: queryParams["q"] })
        } else {
          this.setQuery({ query: '' })
        }

        if ("country" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "country",
            value: queryParams["country"] as string
          });
        }

        if ("region" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "region",
            value: queryParams["region"] as string
          });
        }

        if ("type" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "type",
            value: queryParams["type"] as string
          });
        }

        if ("priceFrom" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "priceFrom",
            value: Number(queryParams["priceFrom"])
          });
        }

        if ("priceTo" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "priceTo",
            value: Number(queryParams["priceTo"])
          });
        }

        if ("startDateFrom" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "startDateFrom",
            value: new Date(`${(queryParams["startDateFrom"] as string)}T00:00:00`)
          });
        }

        if ("startDateTo" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "startDateTo",
            value: new Date(`${(queryParams["startDateTo"] as string)}T00:00:00`)
          });
        }

        if ("endDateFrom" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "endDateFrom",
            value: new Date(`${(queryParams["endDateFrom"] as string)}T00:00:00`)
          });
        }

        if ("endDateTo" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "endDateTo",
            value: new Date(`${(queryParams["endDateTo"] as string)}T00:00:00`)
          });
        }

        if ("company" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "company",
            value: queryParams["company"] as string
          });
        }

        if ("source" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "source",
            value: queryParams["source"] as string
          });
        }

        if ("category" in queryParams) {
          this.$store.dispatch("updateCategoryOption", categoryOption)
          this.$store.dispatch("updateFilter", {
            key: "category",
            value: queryParams["category"] as string
          })
        }

        if ("status" in queryParams) {
          this.$store.dispatch("updateFilter", {
            key: "status",
            value: queryParams["status"] as string
          })
        }

        let pageRefresh = false;
        if (this.pageIsRefreshed) {
          pageRefresh = true
          this.pageIsRefreshed = false
        }

        const searchPayload = prepareSearchPayload(this.page, this.query, this.filters, undefined, pageRefresh)
        if (this.query === '') {
          await this.getNewTenders(searchPayload);
        } else {
          await this.searchTenders(searchPayload);
        }
      },
      immediate: true,
    },
  },
  async created() {
    const bodyElementClasses = document.body.classList;
    bodyElementClasses.remove(...bodyElementClasses);
    bodyElementClasses.add("sidebar-wo-footer");
    bodyElementClasses.add("is-tenders-list");

    // Без этого при переходе на мгновение видно "Поиск не дал результатов"
    this.$store.dispatch("setIsLoading", { isLoading: true })

    const companyTin = this.$route.params.companyTin
    await trackEvent(EventType.company_page_opened, { STIR: companyTin as string})
  },
  unmounted() {
    this.setQueryCompany({ query: '' })
  },
  head() {
    const company = this.company

    if (!company) { return {} }
    return {
      title: this.metaTitle,
      meta: [
        { name: "description", content: this.metaDescription}
      ],
      link: [
        { rel: "canonical", href: this.metaCanonicalLink }
      ],
    }
  },
});
